import React from 'react';
import { toast } from 'sonner';
import {
  BarChart3,
  Check,
  ChevronLeft,
  FileDown,
  FileText,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { confirmDialog } from '../ui/confirm-dialog';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FormattedNumberInput } from './sharedInputs';

function SalesAccountDetails({
  account,
  isExportMode = false,
  handleExportExcel,
  handleExportPDF,
  handleTransactionChange,
  saveTransaction,
  handleManualSave,
  handleAddTransaction,
  handleDeleteTransaction,
  manualInputIndices,
  setEditingTransactionIndex,
  setFocusProductSelectionFilter,
  setIsProductSelectionModalOpen,
}) {
  const currentYear = new Date().getFullYear();
  const annualTarget = account?.annual_target || 0;
  const semiAnnualTarget = annualTarget / 2;
  const quarterlyTarget = annualTarget / 4;
  const monthlyTarget = annualTarget / 12;

  const formatCurrency = (amount) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
  const formatPercentage = (val) => (val !== undefined && !Number.isNaN(val) ? `${val.toFixed(2)}%` : '-');
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const monthlyActuals = {};
  months.forEach((month) => {
    monthlyActuals[month] = 0;
  });

  if (account?.transactions) {
    account.transactions.forEach((transaction) => {
      if (transaction.sales_invoice_date && transaction.net_sales) {
        const date = new Date(transaction.sales_invoice_date);
        if (!Number.isNaN(date.getTime())) {
          const monthName = months[date.getMonth()];
          if (monthName) monthlyActuals[monthName] += parseFloat(transaction.net_sales) || 0;
        }
      }
    });
  }

  const totalActual = Object.values(monthlyActuals).reduce((sum, value) => sum + (value || 0), 0);
  const totalTarget = monthlyTarget * 12;
  const totalDeficit = totalTarget - totalActual;
  const totalPercentage = totalTarget > 0 ? (totalActual / totalTarget) * 100 : 0;
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  const quarterData = quarters.map((quarter) => {
    let quarterMonths = [];
    if (quarter === 'Q1') quarterMonths = ['January', 'February', 'March'];
    else if (quarter === 'Q2') quarterMonths = ['April', 'May', 'June'];
    else if (quarter === 'Q3') quarterMonths = ['July', 'August', 'September'];
    else if (quarter === 'Q4') quarterMonths = ['October', 'November', 'December'];

    const actual = quarterMonths.reduce((sum, month) => sum + (monthlyActuals[month] || 0), 0);
    const deficit = quarterlyTarget - actual;
    const ar = quarterlyTarget > 0 ? (actual / quarterlyTarget) * 100 : 0;
    return { quarter, actual, deficit, ar };
  });

  const processedQuarterData = quarterData.map((data, index) => {
    let adjustedTargetMonth = monthlyTarget;
    let adjustedTargetQuarter = quarterlyTarget;
    if (index > 0) {
      const prevDeficit = quarterData[index - 1].deficit;
      adjustedTargetQuarter += prevDeficit;
      adjustedTargetMonth += prevDeficit / 3;
    }
    return { ...data, adjustedTargetMonth, adjustedTargetQuarter };
  });

  return (
    <div id="sales-performance-report" className="salesora-spr-report relative space-y-8 overflow-x-auto rounded-xl border border-white/10 p-6 md:p-8">
      {!isExportMode && (
        <div className="no-print absolute right-6 top-6 z-10 flex gap-2">
          <Button onClick={handleExportExcel} className="bg-green-600 text-white shadow-lg hover:bg-green-700">
            <FileText className="mr-2 h-4 w-4" />
            Export Excel
          </Button>
          <Button onClick={handleExportPDF} className="bg-red-600 text-white shadow-lg hover:bg-red-700">
            <FileDown className="mr-2 h-4 w-4" />
            Export PDF
          </Button>
        </div>
      )}

      <div className="mb-2 mt-4 text-center">
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-black">{account?.admin_name}</h2>
      </div>
      <h3 className="mb-4 text-center text-xl font-bold uppercase text-black">{currentYear} SALES TARGET</h3>

      <div className="mx-auto mb-4 flex w-full max-w-5xl justify-center gap-2 px-4">
        {[
          { label: 'ANNUAL TARGET', value: annualTarget },
          { label: 'SEMI ANNUAL', value: semiAnnualTarget },
          { label: 'QUARTERLY', value: quarterlyTarget },
          { label: 'MONTHLY TARGET', value: monthlyTarget },
        ].map((item) => (
          <div key={item.label} className="flex-1 overflow-hidden rounded-xl border border-[#1e3a8a] text-center">
            <div className="border-b border-[#1e3a8a] bg-[#1e3a8a] py-1 text-[10px] font-bold uppercase text-white">{item.label}</div>
            <div className="py-1 text-xs font-bold text-gray-600 dark:text-black">{formatCurrency(item.value)}</div>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-4">
        <div className="flex-1 overflow-hidden rounded-xl border border-[#1e3a8a]">
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-[#1e3a8a] text-center text-[10px] font-bold uppercase text-white">
                <th className="w-1/5 border border-[#1e3a8a] px-1 py-1" />
                <th className="w-1/5 border border-[#1e3a8a] px-1 py-1">TARGET</th>
                <th className="w-1/5 border border-[#1e3a8a] px-1 py-1">ACTUAL</th>
                <th className="w-1/5 border border-[#1e3a8a] px-1 py-1">DEFICIT</th>
                <th className="w-1/5 border border-[#1e3a8a] px-1 py-1">PERCENTAGE</th>
              </tr>
            </thead>
            <tbody>
              {months.map((month, monthIndex) => {
                let currentMonthlyTarget = monthlyTarget;
                if (monthIndex >= 3 && monthIndex <= 5) currentMonthlyTarget += quarterData[0].deficit / 3;
                else if (monthIndex >= 6 && monthIndex <= 8) currentMonthlyTarget += quarterData[1].deficit / 3;
                else if (monthIndex >= 9 && monthIndex <= 11) currentMonthlyTarget += quarterData[2].deficit / 3;

                const actual = monthlyActuals[month] || 0;
                const deficit = currentMonthlyTarget - actual;
                const percentage = currentMonthlyTarget > 0 ? (actual / currentMonthlyTarget) * 100 : 0;

                return (
                  <tr key={month} className="h-6 text-center text-black salesora-spr-row">
                    <td className="salesora-month-label border border-[#1e3a8a] bg-gray-50 px-2 text-left font-bold">{month}</td>
                    <td className="salesora-spr-num border border-[#1e3a8a]">{formatCurrency(currentMonthlyTarget)}</td>
                    <td className="salesora-spr-num border border-[#1e3a8a]">{formatCurrency(actual)}</td>
                    <td className={`salesora-spr-num border border-[#1e3a8a] ${deficit > 0 ? 'text-red-600' : 'text-black'}`}>{formatCurrency(deficit)}</td>
                    <td className="salesora-spr-num border border-[#1e3a8a]">{formatPercentage(percentage)}</td>
                  </tr>
                );
              })}
              <tr className="bg-[#1e3a8a] text-center font-bold text-white">
                <td className="border border-[#1e3a8a] py-1">TOTAL</td>
                <td className="border border-[#1e3a8a] py-1">{formatCurrency(totalTarget)}</td>
                <td className="border border-[#1e3a8a] py-1">{formatCurrency(totalActual)}</td>
                <td className="border border-[#1e3a8a] py-1">{formatCurrency(totalDeficit)}</td>
                <td className="border border-[#1e3a8a] py-1">{formatPercentage(totalPercentage)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex-1 overflow-hidden rounded-xl border border-[#1e3a8a]">
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-[#1e3a8a] text-center text-[10px] font-bold uppercase text-white">
                <th className="w-16 border border-[#1e3a8a] px-1 py-1" />
                <th className="border border-[#1e3a8a] px-1 py-1">ACTUAL PERFORMANCE</th>
                <th className="border border-[#1e3a8a] px-1 py-1">AR</th>
                <th className="border border-[#1e3a8a] px-1 py-1">ADJUSTED TARGET PER MONTH</th>
                <th className="border border-[#1e3a8a] px-1 py-1">ADJUSTED TARGET PER QUARTER</th>
                <th className="border border-[#1e3a8a] px-1 py-1">DEFICIT</th>
              </tr>
            </thead>
            <tbody>
              {processedQuarterData.map((row) => (
                <tr key={row.quarter} className="h-6 text-center text-black salesora-spr-row">
                  <td className="salesora-quarter-label border border-[#1e3a8a] bg-gray-50 font-bold">{row.quarter}</td>
                  <td className="salesora-spr-num border border-[#1e3a8a]">{formatCurrency(row.actual)}</td>
                  <td className="salesora-spr-num border border-[#1e3a8a]">{formatPercentage(row.ar)}</td>
                  <td className="salesora-spr-num border border-[#1e3a8a]">{formatCurrency(row.adjustedTargetMonth)}</td>
                  <td className="salesora-spr-num border border-[#1e3a8a]">{formatCurrency(row.adjustedTargetQuarter)}</td>
                  <td className={`salesora-spr-num border border-[#1e3a8a] ${row.deficit > 0 ? 'text-red-600' : 'text-black'}`}>{formatCurrency(row.deficit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h3 className="mb-3 mt-6 text-center text-lg font-bold uppercase text-black">SALES PERFORMANCE DETAILS</h3>

      <div className="overflow-x-auto rounded-xl border border-[#1e3a8a]">
        <table className="w-full min-w-[2000px] border-collapse text-[10px]">
          <thead>
            <tr className="bg-[#1e3a8a] text-center text-[10px] font-bold uppercase leading-tight text-white">
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">SALES INVOICE DATE</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">SALES INVOICE NUMBER</th>
              <th className="w-48 border border-[#1e3a8a] px-1 py-1">ACCOUNT NAME</th>
              <th className="w-64 border border-[#1e3a8a] px-1 py-1">ITEM DESCRIPTION</th>
              <th className="w-20 border border-[#1e3a8a] px-1 py-1">QUANTITY</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">AMOUNT</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">TOTAL AMOUNT</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">MARKETING FUNDS</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1 salesora-spr-netsales salesora-spr-netsales-header">NET SALES<br />(ACTUAL SALES)</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">COLLECTION RECEIPT DATE</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">COLLECTION RECEIPT NO.</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1">AMOUNT COLLECTED</th>
              <th className="w-24 border border-[#1e3a8a] px-1 py-1">1% WHT</th>
              <th className="w-24 border border-[#1e3a8a] px-1 py-1">5% WHT</th>
              <th className="w-24 border border-[#1e3a8a] px-1 py-1">RETENTION</th>
              <th className="w-24 border border-[#1e3a8a] px-1 py-1">OTHERS</th>
              <th className="w-32 border border-[#1e3a8a] px-1 py-1 salesora-spr-total salesora-spr-total-header">TOTAL</th>
              <th className="w-48 border border-[#1e3a8a] px-1 py-1">REMARKS</th>
              {!isExportMode && <th className="no-print w-24 border border-[#1e3a8a] px-1 py-1">ACTIONS</th>}
            </tr>
          </thead>
          <tbody>
            {account?.transactions?.map((transaction, index) => (
              <tr key={transaction.id || index} className="h-6 text-black hover:bg-gray-50 dark:hover:bg-black">
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-center px-1 text-[10px]">{transaction.sales_invoice_date || ''}</div> : <input type="date" className="h-full w-full bg-transparent px-1 text-center focus:bg-blue-50 focus:outline-none" value={transaction.sales_invoice_date || ''} onChange={(e) => handleTransactionChange(index, 'sales_invoice_date', e.target.value)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-center px-1 text-[10px]">{transaction.sales_invoice_number || ''}</div> : <input className="h-full w-full bg-transparent px-1 text-center focus:bg-blue-50 focus:outline-none" value={transaction.sales_invoice_number || ''} onChange={(e) => handleTransactionChange(index, 'sales_invoice_number', e.target.value)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center px-1 text-left text-[10px]">{transaction.account_name || ''}</div> : <input className="h-full w-full bg-transparent px-1 text-left focus:bg-blue-50 focus:outline-none" value={transaction.account_name || ''} onChange={(e) => handleTransactionChange(index, 'account_name', e.target.value)} onBlur={saveTransaction} />}</td>
                <td className="group relative border border-[#1e3a8a] p-0">
                  <div className="relative flex h-full w-full">
                    {isExportMode ? (
                      <div className="flex h-full w-full items-center px-1 text-left text-[10px]">{transaction.item_description || ''}</div>
                    ) : (
                      <>
                        <input className="h-full w-full bg-transparent px-1 text-left focus:bg-blue-50 focus:outline-none" value={transaction.item_description || ''} readOnly={!manualInputIndices.has(index)} onChange={(e) => handleTransactionChange(index, 'item_description', e.target.value)} onBlur={saveTransaction} />
                        <button
                          className="absolute right-0 top-0 z-10 flex h-full items-center justify-center bg-gray-100 px-2 text-gray-600 opacity-0 transition-opacity hover:bg-gray-200 group-hover:opacity-100 dark:text-gray-200"
                          onClick={() => {
                            setEditingTransactionIndex(index);
                            setFocusProductSelectionFilter('');
                            setIsProductSelectionModalOpen(true);
                          }}
                          title="Select Focus Product"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-center px-1 text-[10px]">{transaction.quantity ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.quantity) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-center focus:bg-blue-50 focus:outline-none" value={transaction.quantity || ''} onChange={(val) => handleTransactionChange(index, 'quantity', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.amount ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.amount) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.amount || ''} onChange={(val) => handleTransactionChange(index, 'amount', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.total_amount ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.total_amount) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.total_amount || ''} onChange={(val) => handleTransactionChange(index, 'total_amount', parseFloat(val) || 0)} readOnly onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.marketing_funds ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.marketing_funds) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.marketing_funds || ''} onChange={(val) => handleTransactionChange(index, 'marketing_funds', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0 salesora-spr-netsales">{isExportMode ? <div className="flex h-full w-full items-center justify-end bg-gray-100 px-1 text-[10px]">{transaction.net_sales ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.net_sales) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-gray-100 px-1 text-right focus:outline-none" value={transaction.net_sales || ''} readOnly />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-center px-1 text-[10px]">{transaction.collection_receipt_date || ''}</div> : <input className="h-full w-full bg-transparent px-1 text-center focus:bg-blue-50 focus:outline-none" value={transaction.collection_receipt_date || ''} onChange={(e) => handleTransactionChange(index, 'collection_receipt_date', e.target.value)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-center px-1 text-[10px]">{transaction.collection_receipt_no || ''}</div> : <input className="h-full w-full bg-transparent px-1 text-center focus:bg-blue-50 focus:outline-none" value={transaction.collection_receipt_no || ''} onChange={(e) => handleTransactionChange(index, 'collection_receipt_no', e.target.value)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.amount_collected ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.amount_collected) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.amount_collected || ''} onChange={(val) => handleTransactionChange(index, 'amount_collected', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.wht_1_percent ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.wht_1_percent) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.wht_1_percent || ''} onChange={(val) => handleTransactionChange(index, 'wht_1_percent', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.wht_5_percent ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.wht_5_percent) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.wht_5_percent || ''} onChange={(val) => handleTransactionChange(index, 'wht_5_percent', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.retention ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.retention) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.retention || ''} onChange={(val) => handleTransactionChange(index, 'retention', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex h-full w-full items-center justify-end px-1 text-[10px]">{transaction.others ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.others) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-transparent px-1 text-right focus:bg-blue-50 focus:outline-none" value={transaction.others || ''} onChange={(val) => handleTransactionChange(index, 'others', parseFloat(val) || 0)} onBlur={saveTransaction} />}</td>
                <td className="border border-[#1e3a8a] p-0 salesora-spr-total">{isExportMode ? <div className="flex h-full w-full items-center justify-end bg-gray-100 px-1 text-[10px]">{transaction.total ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(transaction.total) : ''}</div> : <FormattedNumberInput className="h-full w-full bg-gray-100 px-1 text-right focus:outline-none" value={transaction.total || ''} readOnly />}</td>
                <td className="border border-[#1e3a8a] p-0">{isExportMode ? <div className="flex w-[50ch] items-start overflow-hidden whitespace-pre-wrap break-words px-1 text-left text-[10px]">{transaction.remarks || ''}</div> : <textarea className="w-[50ch] resize-none overflow-hidden whitespace-pre-wrap break-words bg-transparent px-1 text-left focus:bg-blue-50 focus:outline-none" rows={1} value={transaction.remarks || ''} onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px`; }} ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }} onChange={(e) => handleTransactionChange(index, 'remarks', e.target.value)} onBlur={saveTransaction} />}</td>
                {!isExportMode && (
                  <td className="no-print border border-[#1e3a8a] p-0 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleManualSave}>
                        <Check className="h-3 w-3 text-green-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditingTransactionIndex(index); setFocusProductSelectionFilter(''); setIsProductSelectionModalOpen(true); }}>
                        <Pencil className="h-3 w-3 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteTransaction(index)}>
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            <tr className="h-6 bg-[#1e3a8a] text-center font-bold text-white">
              <td colSpan={6} className="border border-[#1e3a8a] px-2 text-right">TOTAL</td>
              <td className="border border-[#1e3a8a] px-1 text-right">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.total_amount || 0), 0))}</td>
              <td className="border border-[#1e3a8a] px-1 text-right">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.marketing_funds || 0), 0))}</td>
              <td className="border border-[#1e3a8a] px-1 text-right salesora-spr-netsales salesora-spr-footer">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.net_sales || 0), 0))}</td>
              <td className="border border-[#1e3a8a]" />
              <td className="border border-[#1e3a8a]" />
              <td className="border border-[#1e3a8a] px-1 text-right">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.amount_collected || 0), 0))}</td>
              <td className="border border-[#1e3a8a] px-1 text-right">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.wht_1_percent || 0), 0))}</td>
              <td className="border border-[#1e3a8a] px-1 text-right">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.wht_5_percent || 0), 0))}</td>
              <td className="border border-[#1e3a8a] px-1 text-right">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.retention || 0), 0))}</td>
              <td className="border border-[#1e3a8a] px-1 text-right">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.others || 0), 0))}</td>
              <td className="border border-[#1e3a8a] px-1 text-right salesora-spr-total salesora-spr-footer">{formatCurrency((account?.transactions || []).reduce((sum, t) => sum + (t.total || 0), 0))}</td>
              <td className="border border-[#1e3a8a]" />
              {!isExportMode && <td className="no-print border border-[#1e3a8a]" />}
            </tr>
          </tbody>
        </table>
      </div>

      {!isExportMode && (
        <div className="no-print mt-4 flex justify-end">
          <Button onClick={handleAddTransaction} className="bg-blue-900 text-white hover:bg-blue-800">
            <Plus className="mr-2 h-4 w-4" />
            Add Field
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SalesPerformanceSubmenu(props) {
  const {
    activeMenuItem,
    user,
    admins,
    salesAccounts,
    setSalesAccounts,
    setSelectedSalesAccount,
    selectedSalesAccount,
    setActiveMenuItem,
    axiosInstance,
    fetchBluePrints,
    showAddSalesDropdown,
    setShowAddSalesDropdown,
    targetAccount,
    setTargetAccount,
    targetAmount,
    setTargetAmount,
    isSetTargetDialogOpen,
    setIsSetTargetDialogOpen,
    handleExportAllPDF,
    handleExportPDF,
    handleExportExcel,
    handleTransactionChange,
    saveTransaction,
    handleManualSave,
    handleAddTransaction,
    handleDeleteTransaction,
    manualInputIndices,
    setEditingTransactionIndex,
    setFocusProductSelectionFilter,
    setIsProductSelectionModalOpen,
  } = props;

  if (activeMenuItem === 'project-details-info') {
    return (
      <div className="nad-modal-content content-glass rounded-2xl shadow-2xl border border-white/5 p-5 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sales Performance Report</h2>
            <p className="text-gray-600 dark:text-gray-200">Track and monitor sales performance by admin</p>
          </div>

          <div className="relative flex gap-2">
            {user?.is_super_admin && (
              <>
                <Button className="bg-red-600 hover:bg-red-700" onClick={handleExportAllPDF}>
                  <FileDown className="mr-2 h-4 w-4" />
                  Export All PDF
                </Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowAddSalesDropdown(!showAddSalesDropdown)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Sales
                </Button>

                {showAddSalesDropdown && (
                  <div className="absolute right-0 z-50 mt-2 w-56 rounded-md border border-gray-200 bg-[color:var(--nad-modal-bg)] nad-modal-content backdrop-blur-md shadow-lg">
                    <div className="max-h-60 overflow-y-auto py-1">
                      {admins.map((admin) => (
                        <button
                          key={admin.id}
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-black"
                          onClick={async () => {
                            try {
                              if (!salesAccounts.find((account) => account.id === admin.id)) {
                                const response = await axiosInstance.post('/sales-accounts', {
                                  admin_id: admin.id,
                                  admin_name: admin.full_name,
                                });
                                const newAccount = {
                                  ...response.data,
                                  full_name: response.data.admin_name,
                                  id: response.data.admin_id,
                                };
                                setSalesAccounts([newAccount, ...salesAccounts]);
                                toast.success(`Added ${admin.full_name} to sales report`);
                                fetchBluePrints();
                              } else {
                                toast.info(`${admin.full_name} is already in the report`);
                              }
                              setShowAddSalesDropdown(false);
                            } catch (error) {
                              console.error('Error adding sales account:', error);
                              toast.error(error.response?.data?.detail || 'Failed to add sales account');
                            }
                          }}
                        >
                          {admin.full_name}
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-300">({admin.is_super_admin ? 'Super Admin' : 'Admin'})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {salesAccounts.length === 0 ? (
          <Card className="glass border-0 shadow-lg">
            <CardContent className="py-12">
              <div className="text-center">
                <BarChart3 className="mx-auto mb-4 h-16 w-16 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">No Sales Accounts Added</h3>
                <p className="text-gray-500 dark:text-gray-300">Click "Add Sales" to add admins to the performance report.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {salesAccounts.map((account) => (
              <Card
                key={account.id}
                className={`glass cursor-pointer border-0 shadow-lg transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl ${!user?.is_super_admin && user?.id !== account.admin_id ? 'opacity-70' : ''}`}
                onClick={() => {
                  if (user?.is_super_admin || user?.id === account.admin_id) {
                    setSelectedSalesAccount(account);
                    setActiveMenuItem('sales-account-details');
                  } else {
                    toast.error('You can only access your own sales account.');
                  }
                }}
              >
                <CardHeader className="flex flex-col items-center space-y-2 pb-2 text-center">
                  <CardTitle className="w-full break-words text-center text-3xl font-bold">{account.full_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="text-2xl font-bold">
                        {new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(
                          (account.transactions || []).reduce((sum, transaction) => {
                            if (transaction.sales_invoice_date && transaction.net_sales) {
                              const date = new Date(transaction.sales_invoice_date);
                              if (!Number.isNaN(date.getTime())) {
                                return sum + (parseFloat(transaction.net_sales) || 0);
                              }
                            }
                            return sum;
                          }, 0)
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">Total Actual Performance</p>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-100">
                        Target: {new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(account.annual_target || 0)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-2">
                      {(user?.is_super_admin || user?.id === account.admin_id) && (
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSalesAccount(account);
                            setActiveMenuItem('sales-account-details');
                          }}
                        >
                          View Report
                        </Button>
                      )}
                      {user?.is_super_admin && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTargetAccount(account);
                              setTargetAmount(account.annual_target || '');
                              setIsSetTargetDialogOpen(true);
                            }}
                          >
                            Set Target
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-0 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-black"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const confirmed = await confirmDialog({
                                title: 'Remove Account',
                                description: `Remove ${account.full_name} from report?`,
                                variant: 'destructive',
                              });
                              if (!confirmed) return;
                              try {
                                await axiosInstance.delete(`/sales-accounts/${account.id}`);
                                setSalesAccounts(salesAccounts.filter((item) => item.id !== account.id));
                                toast.success('Removed sales account');
                              } catch (error) {
                                console.error('Error removing sales account:', error);
                                toast.error('Failed to remove sales account');
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={isSetTargetDialogOpen} onOpenChange={setIsSetTargetDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set Annual Target</DialogTitle>
              <DialogDescription>Enter the annual sales target for {targetAccount?.full_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Annual Target Amount (PHP)</Label>
                <Input type="number" placeholder="0.00" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsSetTargetDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  try {
                    const response = await axiosInstance.put(`/sales-accounts/${targetAccount.admin_id}`, {
                      annual_target: parseFloat(targetAmount),
                    });
                    setSalesAccounts(salesAccounts.map((account) => (account.id === targetAccount.id ? { ...account, annual_target: response.data.annual_target } : account)));
                    setIsSetTargetDialogOpen(false);
                    toast.success('Annual target updated');
                  } catch (error) {
                    console.error('Error updating target:', error);
                    toast.error('Failed to update target');
                  }
                }}
              >
                Save Target
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (activeMenuItem !== 'sales-account-details' || !selectedSalesAccount) {
    return null;
  }

  return (
    <div className="nad-modal-content content-glass rounded-2xl shadow-2xl border border-white/5 p-5 md:p-6 space-y-5">
      <div className="flex items-center">
        <Button variant="ghost" onClick={() => setActiveMenuItem('project-details-info')} className="mr-4">
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>
      <SalesAccountDetails
        account={selectedSalesAccount}
        handleExportExcel={handleExportExcel}
        handleExportPDF={handleExportPDF}
        handleTransactionChange={handleTransactionChange}
        saveTransaction={saveTransaction}
        handleManualSave={handleManualSave}
        handleAddTransaction={handleAddTransaction}
        handleDeleteTransaction={handleDeleteTransaction}
        manualInputIndices={manualInputIndices}
        setEditingTransactionIndex={setEditingTransactionIndex}
        setFocusProductSelectionFilter={setFocusProductSelectionFilter}
        setIsProductSelectionModalOpen={setIsProductSelectionModalOpen}
      />
    </div>
  );
}
